import BaseModel from './BaseModel';
import User from './User';
import { Utils } from '../services';
import * as _ from 'es-toolkit';

/** Class representing a sector. */
class Sector extends BaseModel {

    constructor(properties) {
        super(properties);

        if (this.parent)
            this.parent = new Sector(this.parent);

        this.children = (this.children || []).map(props => new Sector(props));

        if (this.admins) {
            this.admins = (this.admins || []).map(props => new User(props));
            this.admin_ids = this.admin_ids || this.admins.map(user => user.id);
        }
    }

    /**
     * Build a tree of sectors
     *
     * @param {Sector[]} sectors
     * @param {String} lang
     *
     * @return Sector[]
     */
    static buildTree(sectors, lang) {
        if (!sectors)
            return [];

        // Clone so we can attach `depth` without mutating the store/API models.
        sectors = _.cloneDeep(sectors);

        const data        = [];
        const parents     = sectors.filter(s => !s.parent_id);
        const by_parent   = sectors.reduce((acc, s) => {
            const key = s.parent_id || '__root__';
            if (!acc[key])
                acc[key] = [];
            acc[key].push(s);
            return acc;
        }, {});

        const addToTree = (sector, depth) => {
            sector.depth = depth;
            data.push(sector);

            // build children from flat list using parent_id, no lazy-loaded children needed
            (by_parent[sector.id] || [])
                .sort(Sector.sort(lang))
                .forEach(s => addToTree(s, depth + 1));
        };

        // let's add all the root sectors
        parents
            .sort(Sector.sort(lang))
            .forEach(sector => addToTree(sector, 0));

        return data;
    }

    /**
     * Generate a sort function based on the provided language
     *
     * @param {String} lang
     *
     * @return {Callable}
     */
    static sort(lang) {
        return (a, b) => Utils.localized_or_fallback(a.name, lang).localeCompare(
            Utils.localized_or_fallback(b.name, lang)
        );
    }

    /**
     * Keep soft-deleted (or otherwise missing) sectors in a select list when they are
     * still referenced by the current form value.
     *
     * @param {Array} sectors
     * @param {Array|object|null} extras
     * @return {Array}
     */
    static ensurePresent(sectors, extras) {
        const list = [...(sectors || [])];
        const byId = new Map(list.filter(s => s?.id).map(s => [s.id, s]));
        const candidates = Array.isArray(extras) ? extras : [extras];

        candidates.forEach((sector) => {
            if (sector?.id && !byId.has(sector.id)) {
                byId.set(sector.id, sector);
                list.push(sector);
            }
        });

        return list;
    }

    /**
     * Collect all descendant sector ids for a parent sector.
     *
     * @param {Sector[]} sectors
     * @param {String} sector_id
     *
     * @return {String[]}
     */
    static collectDescendantIds(sectors, sector_id) {
        if (!sectors?.length || !sector_id)
            return [];

        const by_parent = sectors.reduce((acc, sector) => {
            const key = sector.parent_id || '__root__';
            if (!acc[key])
                acc[key] = [];
            acc[key].push(sector.id);
            return acc;
        }, {});

        const descendants = [];
        const walk = (id) => {
            (by_parent[id] || []).forEach((child_id) => {
                descendants.push(child_id);
                walk(child_id);
            });
        };
        walk(sector_id);

        return descendants;
    }

    /**
     * Expand selected sector ids to include all descendants (parent filter).
     *
     * @param {Sector[]} sectors
     * @param {String[]} selected_ids
     *
     * @return {String[]}
     */
    static expandSectorIds(sectors, selected_ids) {
        const expanded = new Set();
        (selected_ids || []).forEach((id) => {
            if (!id)
                return;
            expanded.add(id);
            Sector.collectDescendantIds(sectors, id).forEach((child_id) => expanded.add(child_id));
        });
        return Array.from(expanded);
    }
}

export default Sector;
